// src/utils/paidOrders.js
//
// The lifecycle of an order that has been CHARGED but not yet handed to the
// customer (client request 2026-08-26).
//
// Lives in utils/ rather than pos-app/ because BOTH tills need it — the native
// surface and the legacy web/Electron page — and neither should import across
// the other's boundary. pos-app/surfaces/cashier/lib/pendingOrderFlow.js
// re-exports these so that surface keeps one import point, the same pattern
// transactionFlow.js uses for utils/transactionLines.js.
import { format } from 'date-fns';
import { occupationService, transactionService } from '../services/api';
import tenantStorage from '../services/tenantStorage';

// ---------------------------------------------------------------------------
// createPaidOrder — the CHARGE-a-table-order flow (client request 2026-08-26).
//
// Charging a table/tent order takes payment and closes the transaction, but
// the food still has to reach the customer. This writes the settled order into
// the SAME pending-order store a held order uses, flagged `paid`, so it stays
// visible as a hand-over queue until the cashier taps Clear.
//
// It deliberately does NOT mirror createPendingOrder's Step 2: the charge path
// already decremented stock when the transaction posted, and deducting again
// here would double-count every sale. There is no collision guard either — the
// transaction is already banked, so refusing to record the order would lose the
// hand-over rather than prevent anything.
//
// Occupation needs no call: app/routers/transactions.py:217-229 already runs
// occupation_service.mark_paid for any transaction carrying a dining option and
// table number, and DiningOption.auto_clear_on_payment already decides whether
// the tent number frees immediately or waits for Clear.
// ---------------------------------------------------------------------------
export const createPaidOrder = async (ctx) => {
    const {
        selectedDiningOption, tableNumber,
        setPendingOrders, setPendingOrderIds, setPrintedOrders,
        selectedProducts, cashierName, businessDaySalesDate,
        totals, discountAmount, addServiceCharge, addTax, notes,
        appliedPromo, promoDiscountAmount,
        transactionId, paymentMethod,
    } = ctx;

    const tableKey = `${selectedDiningOption}-${tableNumber}`;

    setPrintedOrders((prev) => {
        const updated = { ...prev };
        delete updated[tableKey];
        return updated;
    });

    const paidOrder = {
        time: format(new Date(), 'HH:mm:ss'),
        date: format(new Date(), 'yyyy-MM-dd'),
        sales_date: (await tenantStorage.getState('salesDate')) || businessDaySalesDate,
        cashier: cashierName,
        diningOption: selectedDiningOption,
        tableNumber,
        products: [...selectedProducts],
        subtotal: totals.subtotal,
        discount: discountAmount,
        serviceCharge: totals.serviceCharge,
        total: Math.max(0, totals.total - promoDiscountAmount),
        notes,
        paymentMethod: paymentMethod || '',
        addServiceCharge,
        addTax,
        tax: totals.tax,
        // Paid orders are settled, not awaiting payment. `paid` is what the
        // Orders panel branches on to suppress Settle/Edit — a paid order must
        // never be chargeable a second time.
        paid: true,
        paidAt: new Date().toISOString(),
        paidTransactionId: transactionId || null,
        is_submitted: true,
        is_printed: false,
        appliedPromoId: appliedPromo?.promotion?.promotion_id || null,
        promoDiscountAmount,
    };

    const currentSalesDate = await tenantStorage.getState('salesDate');
    const response = await tenantStorage.saveOrder(
        tableKey, paidOrder, false, false, null, currentSalesDate
    );

    setPendingOrders((prev) => ({ ...prev, [tableKey]: paidOrder }));
    if (response?.order_id) {
        setPendingOrderIds((prev) => ({ ...prev, [tableKey]: response.order_id }));
    }

    return tableKey;
};

// ---------------------------------------------------------------------------
// clearPaidOrder — the cashier hands the food over.
//
// This is the completion signal for a paid order. It is a button rather than a
// KDS event on purpose: emitOrderCompletedEvent (kds/lib/boardFlow.js) has no
// live listener outside the archive, and a receipts-only tenant has no kitchen
// signal at all — so a thumb is the only mechanism that works everywhere.
//
// Clearing the occupation is best-effort, matching printOrderPreview's rule: a
// network blip must not leave a handed-over order stuck in the queue.
// ---------------------------------------------------------------------------
export const clearPaidOrder = async (orderKey, ctx) => {
    const { setPendingOrders, setPrintedOrders, setPendingOrderIds, activeOccupation } = ctx;

    if (activeOccupation?.id) {
        try {
            await occupationService.clearTable(activeOccupation.id);
        } catch (err) {
            console.error('Failed to clear occupation on hand-over:', err);
        }
    }

    await tenantStorage.deleteOrder(orderKey);

    const drop = (setter) => setter((prev) => {
        const next = { ...prev };
        delete next[orderKey];
        return next;
    });
    drop(setPendingOrders);
    drop(setPrintedOrders);
    drop(setPendingOrderIds);
};

// ---------------------------------------------------------------------------
// deletePaidOrderAction — voiding a sale the customer has already paid for.
//
// There is NO refund concept in this system (spec §4.6). The existing reversal
// is DELETE /transactions/{id} (app/routers/transactions.py:470-530), which
// snapshots the whole sale into `voids` with void_type="transaction", restores
// inventory, and HARD-DELETES the transaction. Because the row is removed
// rather than flagged, the day's takings reconcile against the drawer
// automatically, and the void stays auditable in Portal → Voids with the
// amount preserved in record_data.
//
// Order of operations matters: void FIRST, clear second. If the void fails the
// order stays exactly where it is — a stuck order the cashier can retry is a
// far better outcome than a vanished order whose money is still counted.
//
// `approvedBy` is the manager who authorised it at the gate, not the cashier
// who asked. Recording the cashier would defeat the point of the gate.
// ---------------------------------------------------------------------------
export const deletePaidOrderAction = async (orderKey, reason, ctx) => {
    const {
        order, approvedBy, cashierName,
        setPendingOrders, setPrintedOrders, setPendingOrderIds,
        activeOccupation,
    } = ctx;

    const transactionId = order?.paidTransactionId;
    if (!transactionId) {
        // Clearing without voiding would leave takings overstated by this
        // order's value and the drawer would not reconcile at end of session.
        throw new Error(
            `Cannot void paid order ${orderKey}: no transaction id recorded against it`
        );
    }

    // Not wrapped in try/catch on purpose — a failed void must propagate so
    // the caller keeps the order and surfaces the error.
    await transactionService.deleteTransaction(
        transactionId, reason, approvedBy || cashierName || null
    );

    await clearPaidOrder(orderKey, {
        setPendingOrders, setPrintedOrders, setPendingOrderIds, activeOccupation,
    });
};
